import {
  Button,
  Checkbox,
  DatePicker,
  DateRangePicker,
  Input,
  Radio,
  Select,
  Textarea,
  useConfirm,
  useToast,
} from '@/components';
import { DateTimePicker } from '@/components/DateTimePicker';
import { NumberRangePicker } from '@/components/NumberRangePicker';
import { TimePicker } from '@/components/TimePicker';
import { A } from '@solidjs/router';
import { ApiClientError } from '@uchat-team-management/shared';
import { For, Show, createMemo, createSignal } from 'solid-js';
import type { PollAnswerValue, PollDetail, PollField } from './api';
import { validatePollResponse, visiblePollFieldIds } from './branching';
import { useAddPollOption, useRespondToPoll, useRetractPollResponse } from './hooks';

const isEmpty = (value: PollAnswerValue | undefined) =>
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    (!String(value.start).trim() || !String(value.end).trim()));

export const RespondForm = (props: {
  poll: Pick<
    PollDetail,
    'id' | 'fields' | 'status' | 'myResponse' | 'allowEdit' | 'canViewResults'
  >;
  preview?: boolean;
}) => {
  const initial = Object.fromEntries(
    (props.poll.myResponse?.answers ?? []).map((answer) => [answer.fieldId, answer.value]),
  );
  const [values, setValues] = createSignal<Record<string, PollAnswerValue | undefined>>(initial);
  const [errors, setErrors] = createSignal<Record<string, string>>({});
  const submit = useRespondToPoll(props.poll.id);
  const retract = useRetractPollResponse(props.poll.id);
  const toast = useToast();
  const confirm = useConfirm();
  const visible = createMemo(() => visiblePollFieldIds(props.poll.fields, values()));
  const disabled = () =>
    props.poll.status !== 'open' || Boolean(props.poll.myResponse && !props.poll.allowEdit);

  const setValue = (fieldId: string, value: PollAnswerValue | undefined) => {
    setValues((current) => ({ ...current, [fieldId]: value }));
    setErrors((current) => ({ ...current, [fieldId]: '' }));
  };

  const save = async () => {
    const found = validatePollResponse(props.poll.fields, values());
    setErrors(found);
    if (Object.keys(found).length) return;
    if (props.preview) {
      toast.success('Preview only — no response was submitted');
      return;
    }
    const shown = visible();
    const answers = props.poll.fields
      .filter((field) => field.type !== 'section' && shown.has(field.id))
      .flatMap((field) => {
        const value = values()[field.id];
        return isEmpty(value) ? [] : [{ fieldId: field.id, value: value as PollAnswerValue }];
      });
    try {
      await submit.mutateAsync({ answers });
      toast.success(props.poll.myResponse ? 'Response updated' : 'Response submitted');
    } catch (error) {
      toast.error(
        error instanceof ApiClientError ? error.error.message : 'Could not save response',
      );
    }
  };

  const withdraw = async () => {
    if (
      !(await confirm({
        title: 'Withdraw your response?',
        body: 'Your saved answers will be permanently removed.',
        confirmLabel: 'Withdraw',
        danger: true,
      }))
    )
      return;
    await retract.mutateAsync(undefined as never);
    setValues({});
    toast.success('Response withdrawn');
  };

  return (
    <div class="flex flex-col gap-4">
      <Show when={props.poll.status === 'closed'}>
        <div class="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm dark:border-neutral-800 dark:bg-neutral-900">
          This poll is closed. Your saved answers are read-only.
          <Show when={props.poll.canViewResults}>
            {' '}
            <A
              href={`/polls/${props.poll.id}/results`}
              class="text-brand-600 hover:underline dark:text-brand-400"
            >
              View results
            </A>
          </Show>
        </div>
      </Show>
      <fieldset disabled={disabled()} class="flex flex-col gap-4 disabled:opacity-75">
        <For each={props.poll.fields}>
          {(field) => (
            <Show when={visible().has(field.id)}>
              <Show
                when={field.type !== 'section'}
                fallback={
                  <section class="border-b border-neutral-200 pb-2 pt-4 dark:border-neutral-800">
                    <h2 class="text-lg font-semibold">{field.label}</h2>
                    <Show when={field.helpText}>
                      <p class="mt-1 text-sm text-neutral-500">{field.helpText}</p>
                    </Show>
                  </section>
                }
              >
                <PollQuestion
                  preview={props.preview}
                  pollId={props.poll.id}
                  field={field}
                  value={values()[field.id]}
                  error={errors()[field.id]}
                  disabled={disabled()}
                  onChange={(value) => setValue(field.id, value)}
                />
              </Show>
            </Show>
          )}
        </For>
      </fieldset>
      <Show when={props.poll.status === 'open' && !disabled()}>
        <div class="flex flex-wrap justify-end gap-2">
          <Show when={props.poll.myResponse && props.poll.allowEdit}>
            <Button variant="danger" loading={retract.isPending} onClick={withdraw}>
              Withdraw response
            </Button>
          </Show>
          <Button loading={submit.isPending} onClick={save}>
            {props.poll.myResponse ? 'Update response' : 'Submit response'}
          </Button>
        </div>
      </Show>
      <Show when={props.poll.status === 'open' && props.poll.myResponse && !props.poll.allowEdit}>
        <p class="text-sm text-neutral-500">
          Your response has been submitted and cannot be changed.
        </p>
      </Show>
    </div>
  );
};

export const PollQuestion = (props: {
  pollId: string;
  embedded?: boolean;
  preview?: boolean;
  field: PollField;
  value: PollAnswerValue | undefined;
  error?: string;
  disabled: boolean;
  onChange: (value: PollAnswerValue | undefined) => void;
}) => {
  const addOption = useAddPollOption(props.pollId, props.field.id);
  const toast = useToast();
  const [newOption, setNewOption] = createSignal('');
  const label = () => props.field.label;
  const hint = () => props.field.helpText || undefined;
  const stringValue = () => (typeof props.value === 'string' ? props.value : '');
  const numberValue = () => (typeof props.value === 'number' ? props.value : undefined);
  const range = () =>
    typeof props.value === 'object' && !Array.isArray(props.value)
      ? props.value
      : { start: '', end: '' };
  const add = async () => {
    if (props.preview || !newOption().trim()) return;
    try {
      const response = await addOption.mutateAsync(newOption());
      props.onChange(
        props.field.type === 'multi_choice'
          ? [...(Array.isArray(props.value) ? props.value : []), response.data.id]
          : response.data.id,
      );
      setNewOption('');
    } catch (error) {
      toast.error(error instanceof ApiClientError ? error.error.message : 'Could not add option');
    }
  };

  return (
    <div
      class={
        props.embedded
          ? ''
          : 'rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark'
      }
    >
      <Show when={props.field.type === 'single_choice'}>
        <Radio
          label={label()}
          hint={hint()}
          error={props.error}
          required={props.field.required}
          value={stringValue()}
          onChange={props.onChange}
          options={props.field.options.map((option) => ({ value: option.id, label: option.label }))}
        />
      </Show>
      <Show when={props.field.type === 'dropdown'}>
        <Select
          label={label()}
          hint={hint()}
          error={props.error}
          required={props.field.required}
          value={stringValue()}
          onChange={props.onChange}
          placeholder="Select an option"
          options={props.field.options.map((option) => ({ value: option.id, label: option.label }))}
        />
      </Show>
      <Show when={props.field.type === 'multi_choice'}>
        <fieldset>
          <legend class="mb-2 font-medium text-neutral-700 dark:text-neutral-300">
            {label()}
            {props.field.required ? <span class="ml-0.5 text-red-500">*</span> : null}
          </legend>
          <div class="flex flex-col gap-2">
            <For each={props.field.options}>
              {(option) => {
                const selected = () => (Array.isArray(props.value) ? props.value : []);
                return (
                  <Checkbox
                    label={option.label}
                    checked={selected().includes(option.id)}
                    onChange={(checked) =>
                      props.onChange(
                        checked
                          ? [...selected(), option.id]
                          : selected().filter((id) => id !== option.id),
                      )
                    }
                  />
                );
              }}
            </For>
          </div>
          <Show when={props.error}>
            <p class="mt-1 text-xs text-red-600">{props.error}</p>
          </Show>
          <Show when={!props.error && hint()}>
            <p class="mt-1 text-xs text-neutral-500">{hint()}</p>
          </Show>
        </fieldset>
      </Show>
      <Show when={props.field.type === 'short_text'}>
        <Input
          label={label()}
          hint={hint()}
          error={props.error}
          required={props.field.required}
          value={stringValue()}
          maxlength={props.field.config.maxLength}
          onInput={(e) => props.onChange(e.currentTarget.value)}
        />
      </Show>
      <Show when={props.field.type === 'long_text'}>
        <Textarea
          label={label()}
          hint={hint()}
          error={props.error}
          required={props.field.required}
          value={stringValue()}
          maxlength={props.field.config.maxLength}
          onInput={(e) => props.onChange(e.currentTarget.value)}
        />
      </Show>
      <Show when={props.field.type === 'number'}>
        <Input
          label={label()}
          hint={hint()}
          error={props.error}
          required={props.field.required}
          type="number"
          min={props.field.config.min}
          max={props.field.config.max}
          value={numberValue() ?? ''}
          onInput={(e) =>
            props.onChange(e.currentTarget.value ? Number(e.currentTarget.value) : undefined)
          }
        />
      </Show>
      <Show when={props.field.type === 'scale'}>
        <fieldset>
          <legend class="font-medium text-neutral-700 dark:text-neutral-300">{label()}</legend>
          <div class="mt-2 flex flex-wrap gap-2">
            <For
              each={Array.from(
                {
                  length: Math.min(
                    20,
                    (props.field.config.max ?? 5) - (props.field.config.min ?? 1) + 1,
                  ),
                },
                (_, index) => (props.field.config.min ?? 1) + index,
              )}
            >
              {(value) => (
                <Button
                  size="sm"
                  variant={numberValue() === value ? 'primary' : 'secondary'}
                  onClick={() => props.onChange(value)}
                >
                  {value}
                </Button>
              )}
            </For>
          </div>
          <Show when={props.error}>
            <p class="mt-1 text-xs text-red-600">{props.error}</p>
          </Show>
          <Show when={!props.error && hint()}>
            <p class="mt-1 text-xs text-neutral-500">{hint()}</p>
          </Show>
        </fieldset>
      </Show>
      <Show when={props.field.type === 'number_range'}>
        <NumberRangePicker
          label={label()}
          value={range()}
          min={props.field.config.min}
          max={props.field.config.max}
          required={props.field.required}
          disabled={props.disabled}
          hint={hint()}
          error={props.error}
          onChange={props.onChange}
        />
      </Show>
      <Show when={props.field.type === 'date'}>
        <DatePicker
          label={label()}
          hint={hint()}
          required={props.field.required}
          value={stringValue()}
          onChange={props.onChange}
        />
        <Show when={props.error}>
          <p class="mt-1 text-xs text-red-600">{props.error}</p>
        </Show>
      </Show>
      <Show when={props.field.type === 'date_range'}>
        <DateRangePicker
          min={props.field.config.minDate}
          max={props.field.config.maxDate}
          label={label()}
          hint={hint()}
          required={props.field.required}
          error={props.error}
          value={{ start: String(range().start), end: String(range().end) }}
          onChange={props.onChange}
        />
      </Show>
      <Show when={props.field.type === 'datetime'}>
        <DateTimePicker
          label={label()}
          value={stringValue()}
          required={props.field.required}
          disabled={props.disabled}
          hint={hint()}
          error={props.error}
          onChange={props.onChange}
        />
      </Show>
      <Show when={props.field.type === 'time'}>
        <fieldset disabled={props.disabled}>
          <legend class="mb-2 font-medium">
            {label()}
            {props.field.required ? ' *' : ''}
          </legend>
          <TimePicker
            hour={stringValue().split(':')[0] || '00'}
            minute={stringValue().split(':')[1] || '00'}
            disabled={props.disabled}
            onChange={(hour, minute) =>
              props.onChange(
                `${hour ? hour.padStart(2, '0') : ''}:${minute ? minute.padStart(2, '0') : ''}`,
              )
            }
          />
          <Show when={props.error || hint()}>
            <p class={props.error ? 'text-xs text-red-600' : 'text-xs text-neutral-500'}>
              {props.error || hint()}
            </p>
          </Show>
        </fieldset>
      </Show>
      <Show when={props.field.type === 'file'}>
        <p class="text-sm text-neutral-500">File responses are not supported yet.</p>
      </Show>

      <Show
        when={
          !props.disabled &&
          props.field.config.allowAddOption &&
          ['single_choice', 'multi_choice', 'dropdown'].includes(props.field.type)
        }
      >
        <div class="mt-3 flex gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
          <Input
            aria-label={`Add option to ${label()}`}
            class="flex-1"
            value={newOption()}
            onInput={(e) => setNewOption(e.currentTarget.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            loading={addOption.isPending}
            disabled={props.preview}
            title={props.preview ? 'Adding options is unavailable in preview' : undefined}
            onClick={add}
          >
            Add option
          </Button>
        </div>
      </Show>
    </div>
  );
};
